import React, { useMemo } from 'react'
import { getTime } from 'date-fns'
import { omitBy, isUndefined } from '@golden/utils'
import { ActivityType, ActivityInviteFriendStatusType } from '@golden/gdk-campaign'
import Grid from '@material-ui/core/Grid'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FormField from '../../default/form/FormField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import { FormState, ValueGetter } from '../../../utils/default/FormHook'
import { ActivityFormType, ActivityRequest } from './ActivityRecordDetailForm'
import useT from '../../../i18ns/admin/useT'
import { getValueFromChangeEvent, convertEmptyToUndefined, acceptUndefined, pipe, guaranteeBeKey } from '../../../utils/default/FormHelper'
import { SearchToRequestFunc } from '../../../utils/default/ComplexFlowHook'
import { inviteFriendStatusName } from '../../../constants/default/activityStatusName'

export interface ActivityInviteFriendFormType {
  inviteFriendStatus: ActivityInviteFriendStatusType | '--'
  friend_account: string
}

export const initialForm = (): ActivityInviteFriendFormType => ({
  inviteFriendStatus: '--',
  friend_account: ''
})

export const getValueFromEvent: ValueGetter<ActivityInviteFriendFormType> = {
  inviteFriendStatus: getValueFromChangeEvent,
  friend_account: getValueFromChangeEvent
}

export const formToRequest = (form: ActivityFormType): ActivityRequest => {
  const converted = {
    activity: ActivityType.INVITE_FRIEND,
    account: convertEmptyToUndefined(form.account),
    staff_account: convertEmptyToUndefined(form.agent_account),
    status: form.inviteFriendStatus === '--' ? undefined : form.inviteFriendStatus,
    friend_account: convertEmptyToUndefined(form.friend_account),
    type: form.type,
    start_at: form.time.start === null ? undefined : getTime(form.time.start),
    end_at: form.time.end === null ? undefined : getTime(form.time.end),
    page: 1
  } as ActivityRequest
  return omitBy(converted, isUndefined) as ActivityRequest
}

export const searchToRequest: SearchToRequestFunc<{
  account?: string
  agent_account?: string
  status?: string
  friend_account?: string
}> = (search) => {
  const converted = {
    account: search.account,
    agent_account: search.agent_account,
    friend_account: search.friend_account,
    inviteFriendStatus: acceptUndefined(search.status, pipe(
      (value) => guaranteeBeKey(value, Object.keys(inviteFriendStatusName))
    ))
  }
  return omitBy(converted, isUndefined) as any
}

interface PropTypes {
  context: React.Context<FormState<ActivityFormType>>
}

const TextField = React.memo(MuiTextField)

const ActivityRecordInviteFriendForm: React.FC<PropTypes> = (props) => {
  const { context } = props
  const { t } = useT()

  const statusOptions = useMemo(() => {
    return [{ name: t('common.all'), value: '--' }]
      .concat(
        Object.keys(inviteFriendStatusName)
          .map((key) => key as ActivityInviteFriendStatusType)
          .map((key) => ({ name: t(inviteFriendStatusName[key]), value: key })) as any
      )
  }, [t])

  return (
    <React.Fragment>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="account"
          label={t('common.playerAccount')}
          placeholder={t('placeholder.inputPlayerAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="agent_account"
          label={t('common.agentAccount')}
          placeholder={t('placeholder.inputAgentAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, DropDownProps>
          context={context}
          component={DropDown}
          name="inviteFriendStatus"
          label={t('common.status')}
          options={statusOptions}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="friend_account"
          label={t('common.inviteeFriendAccount')}
          placeholder={t('placeholder.inputPlayerAccount')}
          fullWidth
        />
      </Grid>
    </React.Fragment>
  )
}

export default React.memo(ActivityRecordInviteFriendForm)
